import React, { useEffect, useState } from "react";
import { Spinner, Tab, TabContent, Tabs } from "react-bootstrap";

export type DynamicTabProps<
  T extends DynamicTabContentProps = DynamicTabContentProps,
> = {
  eventKey: string;
  title: string;
  iconClass?: string;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
} & (
  | {
      content: React.FC<T>;
      children?: never;
    }
  | {
      content?: never;
      children?: React.ReactNode;
    }
);

export type DynamicTabContentProps = {
  onStateChange: ({
    isHidden,
    hasError,
    isLoading,
  }: {
    isHidden?: boolean;
    hasError?: boolean;
    isLoading?: boolean;
  }) => void;
};

export const DynamicTabs = ({
  className,
  children,
  variant,
  onSelectTab,
  selectedTab,
}: {
  className?: string;
  variant?: string;
  children: React.ReactNode;
} & (
  | {
      onSelectTab: (tab: string) => void;
      selectedTab?: string;
    }
  | {
      onSelectTab?: never;
      selectedTab?: never;
    }
)) => {
  const [hasErrorMap, setHasErrorMap] = useState<Record<string, boolean>>({});
  const [isLoadingMap, setIsLoadingMap] = useState<Record<string, boolean>>({});
  const [isHiddenMap, setIsHiddenMap] = useState<Record<string, boolean>>({});
  const [internalSelectedTab, setInternalSelectedTab] = useState<string>();

  selectedTab = selectedTab || internalSelectedTab;
  const setSelectedTab = (tab?: string) => {
    if (onSelectTab && tab) {
      onSelectTab(tab);
    } else {
      setInternalSelectedTab(tab);
    }
  };

  const getStateChangeHandler =
    (eventKey: string) =>
    ({
      isHidden,
      hasError,
      isLoading,
    }: {
      isHidden?: boolean;
      hasError?: boolean;
      isLoading?: boolean;
    }) => {
      if (isHidden !== undefined) {
        setIsHiddenMap((prev) => ({ ...prev, [eventKey]: isHidden }));
      }
      if (hasError !== undefined) {
        setHasErrorMap((prev) => ({ ...prev, [eventKey]: hasError }));
      }
      if (isLoading !== undefined) {
        setIsLoadingMap((prev) => ({ ...prev, [eventKey]: isLoading }));
      }
    };

  const handleSelect = (key: string | null) => {
    if (key && !isHiddenMap[key]) {
      setSelectedTab(key);
    } else {
      const defaultTab = React.Children.toArray(children)
        .filter((child) => React.isValidElement<DynamicTabProps>(child))
        .find((child) => !isHiddenMap[child.props.eventKey])?.props.eventKey;
      setSelectedTab(defaultTab);
    }
  };

  useEffect(() => {
    if (
      !selectedTab ||
      isHiddenMap[selectedTab] ||
      (selectedTab &&
        !React.Children.toArray(children)
          .filter((child) => React.isValidElement<DynamicTabProps>(child))
          .some((child) => child.props.eventKey === selectedTab))
    ) {
      handleSelect(null);
    }
  }, [isHiddenMap, selectedTab]);

  return (
    <Tabs
      variant={variant}
      onSelect={handleSelect}
      className={className}
      activeKey={selectedTab}
    >
      {React.Children.map(children, (child) => {
        const content = React.isValidElement<DynamicTabProps>(child) && (
          <TabContent>
            {child.props.content?.({
              onStateChange: getStateChangeHandler(child.props.eventKey),
            })}
            {child.props.children}
          </TabContent>
        );

        return (
          content &&
          (isHiddenMap[child.props.eventKey] ? (
            <Tab
              key={child.props.eventKey}
              eventKey={child.props.eventKey}
              title=""
              disabled={true}
              hidden={true}
              tabClassName="d-none"
              mountOnEnter={child.props.mountOnEnter}
              unmountOnExit={child.props.unmountOnExit}
            >
              {content}
            </Tab>
          ) : (
            <Tab
              key={child.props.eventKey}
              eventKey={child.props.eventKey}
              mountOnEnter={child.props.mountOnEnter}
              unmountOnExit={child.props.unmountOnExit}
              title={
                <>
                  {child.props.iconClass && (
                    <i className={`${child.props.iconClass} me-2`} />
                  )}
                  {child.props.title}
                  {hasErrorMap[child.props.eventKey] && (
                    <i className="bi-exclamation-circle-fill text-danger ms-2" />
                  )}
                  {isLoadingMap[child.props.eventKey] && (
                    <Spinner size="sm" className="ms-2" />
                  )}
                </>
              }
            >
              {content}
            </Tab>
          ))
        );
      })}
    </Tabs>
  );
};

export const DynamicTab: React.FC<DynamicTabProps> = () => {
  throw new Error(
    "DynamicTab is an abstract component that does not render independently of DynamicTabs"
  );
};
